import { useCallback, useEffect, useState } from "react";
import { getNotes, updateNotes, duplicateNote } from "../../../network/network.jsx";
import { toast } from "sonner";
import { BoardNote } from "@/bryntum-lib/models/BoardNote.js";
import { useConfirm } from "@omit/react-confirm-dialog";
import { getSavedBoardSelection } from "../../hooks/useBoards";
import { getIsArchivedForBoard } from "../../../other/utils/otherUtils";
import { EntityRecord, WidgetParamKeys } from "@/types/entity";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { ENTITY_TYPES } from "@/other/constants/OtherKeys.jsx";

export interface INoteApiPayload {
  content: string;
  board: number;
  archived: boolean;
}

export interface LoadingStates {
  savingNote: boolean;
  deletingNote: boolean;
  duplicatingNote: boolean;
  loadingNoteData: boolean;
}

export type TUseSwivuNotesReturn = ReturnType<typeof useSwivuNotes>;

export const useSwivuNotes = ({ entityRecord }: { entityRecord: EntityRecord }) => {
  const { project, user, entityManager } = useSwivuBryntumContext();
  const { copyEntityLink } = entityManager;
  const boardNotesStore = project.boardNotesStore;
  const [loading, setLoading] = useState<LoadingStates>({
    savingNote: false,
    deletingNote: false,
    duplicatingNote: false,
    loadingNoteData: false,
  });
  const [notes, setNotes] = useState<BoardNote[]>([]);
  const confirm = useConfirm();
  const hasNotes = notes.length > 0;

  const canEditNote = useCallback(
    (noteRecord: BoardNote): boolean => {
      return user ? user.can(noteRecord.canEdit) : false;
    },
    [user]
  );

  const canViewNote = useCallback(
    (noteRecord: BoardNote): boolean => {
      return user ? user.can(noteRecord.canView) : false;
    },
    [user]
  );

  const saveNote = useCallback(async (noteRecord: BoardNote, data: INoteApiPayload): Promise<void> => {
    try {
      setLoading((prev) => ({ ...prev, savingNote: true }));
      if (noteRecord.isPhantom) {
        await new Promise((res: (value?: unknown) => void) => {
          boardNotesStore.on(
            {
              async idChange({ record }: { record: BoardNote }) {
                if (record.id === noteRecord.id) {
                  await updateNotes(noteRecord.id, data);
                  res();
                }
              },
              once: true
            },
            this
          );
          boardNotesStore.add(noteRecord);
        });
      } else {
        await updateNotes(noteRecord.id, data);
      }
      toast("Note saved successfully");
    } catch (error) {
      toast.error("Something went wrong! Unable to save note");
      console.error("Failed to save note:", error);
      throw error;
    } finally {
      setLoading((prev) => ({ ...prev, savingNote: false }));
    }
  }, [boardNotesStore]);

  const updateNote = useCallback((noteRecord: BoardNote, data: Partial<BoardNote>): void => {
    noteRecord.set(data);
  }, []);

  const deleteNote = useCallback(
    async (recordId: string | number): Promise<boolean> => {
      const noteRecord = boardNotesStore.getById(recordId) as unknown as BoardNote;
      if (!noteRecord) return false;

      const confirmed = await confirm({
        title: "Delete note",
        description: `Are you sure you want to delete "${noteRecord.name || "this note"}"? This action cannot be undone.`,
        confirmButton: {
          variant: "destructive",
          title: "Delete note"
        },
        cancelButton: {
          variant: "ghost"
        }
      });

      if (!confirmed) return false;

      try {
        setLoading((prev) => ({ ...prev, deletingNote: true }));
        boardNotesStore.remove(recordId);
        toast.success("Note successfully deleted");
        return true;
      } catch (error) {
        console.error("Failed to delete note:", error);
        toast.error("Something went wrong! Unable to delete note");
        throw error;
      } finally {
        setLoading((prev) => ({ ...prev, deletingNote: false }));
      }
    },
    [confirm, boardNotesStore]
  );

  const duplicateNoteRecord = useCallback(
    async (noteRecord: BoardNote): Promise<void> => {
      try {
        setLoading((prev) => ({ ...prev, duplicatingNote: true }));

        // Create a phantom record for the duplicate
        const notePhantomRecord = boardNotesStore.createRecord({
          [entityRecord.entityType]: entityRecord.id,
          name: `Copy of ${noteRecord.name}`,
          archived: noteRecord.archived
        }) as unknown as BoardNote;

        // Add the phantom record to the store
        boardNotesStore.add(notePhantomRecord);

        // Listen for the ID change to make the API call
        const detacher = boardNotesStore.on("idChange", async ({ record }: { record: BoardNote }) => {
          if (record.id === notePhantomRecord.id) {
            try {
              await duplicateNote(notePhantomRecord.id, {
                board: noteRecord.boardId,
                archived: noteRecord.archived,
                duplicate_id: noteRecord.id
              });
              toast.success("Note duplicated");
            } catch (error) {
              console.error("Failed to duplicate note:", error);
              toast.error("Something went wrong! Unable to duplicate note");
            } finally {
              detacher();
              setLoading((prev) => ({ ...prev, duplicatingNote: false }));
            }
          }
        });
      } catch (error) {
        console.error("Failed to create duplicate note:", error);
        toast.error("Something went wrong! Unable to duplicate note");
        setLoading((prev) => ({ ...prev, duplicatingNote: false }));
        throw error;
      }
    },
    [boardNotesStore, entityRecord, duplicateNote]
  );

  const createNewNote = useCallback((entityRecord: EntityRecord) => {
    const newNote = boardNotesStore.createRecord({
      [entityRecord.entityType]: entityRecord.id
    }) as unknown as BoardNote;
    return newNote;
  }, [boardNotesStore]);

  const copyNoteLink = useCallback(
    async (noteRecord: BoardNote) => {
      const entityType = noteRecord.taskId ? ENTITY_TYPES.TASK : noteRecord.resourceId ? ENTITY_TYPES.RESOURCE : ENTITY_TYPES.CLIENT;
      await copyEntityLink(WidgetParamKeys.NOTE.EDIT, noteRecord.id, `Note link copied successfully!`, undefined, { entityType, entityId: entityRecord.id });
    },
    [copyEntityLink, entityRecord]
  );

  const getNoteData = useCallback(async (recordId: string | number): Promise<string | null> => {
    try {
      setLoading((prev) => ({ ...prev, loadingNoteData: true }));
      const controller = new AbortController();
      const { data } = await getNotes(recordId, controller);
      return data?.content || null;
    } catch (error) {
      if ((error as Error)?.message === "canceled") return null;
      console.error("Failed to load note:", error);
      toast.error("Something went wrong! Unable to retrieve note");
      return null;
    } finally {
      setLoading((prev) => ({ ...prev, loadingNoteData: false }));
    }
  }, [getNotes]);

  const refreshNotes = useCallback(() => {
    const selectedBoard = getSavedBoardSelection();
    const canShowArchivedRecords = getIsArchivedForBoard(selectedBoard.slug);

    const notes = boardNotesStore.query((record) => {
      return record[entityRecord.entityType] === entityRecord.id && !record.isPhantom && (canShowArchivedRecords || !record.archived);
    }, true) as unknown as BoardNote[];

    const sortedNotes = notes.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    setNotes(sortedNotes);
  }, [boardNotesStore, entityRecord, setNotes]);

  useEffect(() => {
    refreshNotes();
  }, [refreshNotes, entityRecord]);

  return {
    saveNote,
    deleteNote,
    duplicateNoteRecord,
    createNewNote,
    copyNoteLink,
    loading,
    getNoteData,
    refreshNotes,
    notes,
    updateNote,
    canEditNote,
    boardNotesStore,
    hasNotes,
    canViewNote
  };
};
